import type { DocumentData } from '@ps/functions/firebase/firestore';

/**
 * Shared live subscription to a single tipping goal doc
 * (`stores/{sellerId}/tippingGoals/{goalId}`) — the ONE Firestore listener both
 * the owner editor (`subscribeToTippingGoal`, which projects the full
 * `TippingGoalRecord`) and the visitor tipping surfaces (which read only
 * `achievedAmount`) subscribe through, so both sides update on the same event.
 *
 * Loads `@ps/functions/firebase/firestore` lazily — visitor-facing bio defers
 * heavy JS until it's actually needed.
 */
export function subscribeToTippingGoalDoc(
  sellerId: string,
  goalId: string,
  onData: (data: DocumentData | undefined, id: string) => void,
  onError?: (error: Error) => void,
): () => void {
  let disposed = false;
  let unsubscribe: (() => void) | undefined;

  import('@ps/functions/firebase/firestore')
    .then(({ doc, firestore, onSnapshot }) => {
      if (disposed) return;
      const db = firestore();
      if (!db) return;
      const ref = doc(db, 'stores', sellerId, 'tippingGoals', goalId);
      unsubscribe = onSnapshot(
        ref,
        snap => onData(snap.exists() ? snap.data() : undefined, snap.id),
        error => onError?.(error),
      );
    })
    .catch((error: unknown) => {
      console.error(
        '[subscribeToTippingGoalDoc] firebase firestore load failed:',
        error,
      );
    });

  return () => {
    disposed = true;
    unsubscribe?.();
  };
}
